/**
 * Prepare a photo for AI: downscale and re-encode as JPEG in the browser.
 * Re-encoding through a canvas drops EXIF metadata (GPS location, device info).
 * Photos are never stored by the app.
 */
export interface PreparedImage {
  mimeType: 'image/jpeg';
  base64: string;
  previewUrl: string;
}

const MAX_SIDE = 1280;
const QUALITY = 0.82;

export async function prepareImage(file: File, maxSide = MAX_SIDE): Promise<PreparedImage> {
  if (!file.type.startsWith('image/')) throw new Error('Choose an image file');
  const bitmap = await createImageBitmap(file, { imageOrientation: 'from-image' });
  const scale = Math.min(1, maxSide / Math.max(bitmap.width, bitmap.height));
  const width = Math.max(1, Math.round(bitmap.width * scale));
  const height = Math.max(1, Math.round(bitmap.height * scale));
  const canvas = document.createElement('canvas');
  canvas.width = width;
  canvas.height = height;
  const ctx = canvas.getContext('2d');
  if (!ctx) throw new Error('Could not process the photo');
  ctx.drawImage(bitmap, 0, 0, width, height);
  bitmap.close();
  const blob = await new Promise<Blob>((resolve, reject) =>
    canvas.toBlob((b) => (b ? resolve(b) : reject(new Error('Could not process the photo'))), 'image/jpeg', QUALITY),
  );
  const base64 = await new Promise<string>((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result).split(',')[1] ?? '');
    reader.onerror = () => reject(new Error('Could not read the photo'));
    reader.readAsDataURL(blob);
  });
  return { mimeType: 'image/jpeg', base64, previewUrl: URL.createObjectURL(blob) };
}
